import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, RefreshCw, Users, Briefcase } from "lucide-react";
import { api, apiError, eur } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

const NEXT_STATUS = ["arrived", "on_board", "completed"];
const SHIFTS = ["morning", "afternoon", "evening", "night"];
const nextDays = (n) => Array.from({ length: n }, (_, i) => new Date(Date.now() + i * 864e5).toISOString().slice(0, 10));

export default function DriverPortal() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [tab, setTab] = useState("available");
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [schedule, setSchedule] = useState({});
  const [loading, setLoading] = useState(true);
  const reqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqRef.current;
    setLoading(true);
    try {
      if (tab === "schedule") {
        const [sch, s] = await Promise.all([
          api.get("/vendor/schedule", { params: { start: nextDays(1)[0], days: 14 } }),
          api.get("/vendor/summary"),
        ]);
        if (seq !== reqRef.current) return;
        const map = {};
        (sch.data.availability || []).forEach((a) => { map[a.date] = { off: a.off, shifts: a.shifts || [] }; });
        setSchedule(map);
        setSummary(s.data);
      } else {
        const [j, s] = await Promise.all([
          api.get(`/vendor/jobs?scope=${tab}`),
          api.get("/vendor/summary"),
        ]);
        if (seq !== reqRef.current) return;
        setJobs(j.data.jobs || []);
        setSummary(s.data);
      }
    } catch (e) {
      if (seq === reqRef.current) toast.error(apiError(e));
    }
    if (seq === reqRef.current) setLoading(false);
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const saveDay = async (date, off, shifts) => {
    setSchedule((m) => ({ ...m, [date]: { off, shifts } }));
    try {
      await api.post("/vendor/schedule/day", { date, off, shifts });
      toast.success(t("sch.saved"));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const accept = async (b) => {
    try {
      await api.post(`/vendor/jobs/${b.booking_id}/accept`);
      toast.success(t("driver.accepted"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setStatus = async (b, status) => {
    try {
      await api.post(`/vendor/jobs/${b.booking_id}/status`, { status });
      toast.success(t("driver.statusUpdated"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleAvailability = async () => {
    const next = !(summary?.available === 1 || summary?.available === true);
    try {
      await api.post("/vendor/availability", { available: next });
      setSummary((s) => ({ ...s, available: next ? 1 : 0 }));
      toast.success(t("driver.statusUpdated"));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const shareLocation = (b) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.post("/vendor/location", {
            booking_id: b.booking_id,
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
          toast.success(t("driver.statusUpdated"));
        } catch (e) {
          toast.error(apiError(e));
        }
      },
      () => toast.error(t("common.error"))
    );
  };

  const kpis = summary
    ? [
        { label: t("driver.assigned"), value: summary.assigned },
        { label: t("driver.completed"), value: summary.completed },
        { label: t("driver.earnings"), value: eur(summary.earnings_eur) },
        { label: t("driver.commission"), value: `${summary.commission_pct}%` },
        { label: t("driver.available"), value: summary.available },
      ]
    : [];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-6xl px-5 py-12 lg:px-10 lg:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="label-eyebrow">{user?.name}</p>
            <h1 className="mt-2 font-display text-3xl tracking-tight sm:text-4xl" data-testid="driver-title">
              {t("driver.title")}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {summary && (() => {
              const online = summary.available === 1 || summary.available === true;
              return (
                <button
                  type="button"
                  data-testid="availability-toggle"
                  onClick={toggleAvailability}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                    online
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      : "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100"
                  }`}
                >
                  <span className={`h-2 w-2 rounded-full ${online ? "bg-emerald-500" : "bg-slate-400"}`} />
                  {online ? t("ops.online") : t("ops.offline")}
                </button>
              );
            })()}
            <Button variant="outline" data-testid="driver-refresh-btn" onClick={load}>
              <RefreshCw size={15} className="mr-1.5" /> {t("common.refresh")}
            </Button>
          </div>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="driver-kpis">
          {kpis.map((k) => (
            <div key={k.label} className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="text-xs text-slate-500">{k.label}</p>
              <p className="mt-1.5 font-display text-2xl text-slate-900">{k.value}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 inline-flex gap-2 rounded-md bg-slate-100 p-1 text-sm">
          {[
            { v: "available", l: t("driver.open") },
            { v: "mine", l: t("driver.mine") },
            { v: "schedule", l: t("sch.mySchedule") },
          ].map((x) => (
            <button
              key={x.v}
              data-testid={`driver-tab-${x.v}`}
              onClick={() => setTab(x.v)}
              className={`rounded px-5 py-1.5 font-medium transition-colors duration-200 ${
                tab === x.v ? "bg-white text-sea shadow-sm" : "text-slate-500"
              }`}
            >
              {x.l}
            </button>
          ))}
        </div>

        {loading && <p className="mt-10 text-slate-500">{t("common.loading")}</p>}

        {tab === "schedule" && !loading && (
          <div className="mt-6" data-testid="driver-schedule">
            <p className="text-sm text-slate-600">{t("sch.schedSub")}</p>
            <div className="mt-4 space-y-2">
              {nextDays(14).map((date) => {
                const day = schedule[date] || { off: false, shifts: SHIFTS };
                const wd = new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: "short" });
                const toggleShift = (s) => {
                  const has = day.shifts.includes(s);
                  const shifts = has ? day.shifts.filter((x) => x !== s) : [...day.shifts, s];
                  saveDay(date, day.off, shifts);
                };
                return (
                  <div key={date} data-testid={`schedule-day-${date}`} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
                    <div className="w-28 shrink-0">
                      <p className="text-sm font-medium text-slate-900">{date.slice(5)}</p>
                      <p className="text-[11px] text-slate-400">{wd}</p>
                    </div>
                    <button
                      data-testid={`schedule-off-${date}`}
                      onClick={() => saveDay(date, !day.off, day.shifts)}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold ${day.off ? "border-amber-200 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-500"}`}
                    >
                      {t("sch.dayOff")}
                    </button>
                    <div className={`flex flex-wrap gap-1.5 ${day.off ? "pointer-events-none opacity-40" : ""}`}>
                      {SHIFTS.map((s) => (
                        <button
                          key={s}
                          data-testid={`schedule-shift-${date}-${s}`}
                          onClick={() => toggleShift(s)}
                          className={`rounded-md border px-2.5 py-1 text-xs font-medium ${day.shifts.includes(s) ? "border-sea bg-sea/10 text-sea" : "border-slate-200 text-slate-400"}`}
                        >
                          {t(`sch.${s}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab !== "schedule" && !loading && jobs.length === 0 && (
          <p className="mt-10 text-slate-500" data-testid="driver-jobs-empty">
            {t("driver.noJobs")}
          </p>
        )}

        {tab !== "schedule" && (
        <div className="mt-6 space-y-4" data-testid="driver-jobs-list">
          {jobs.map((b) => (
            <article
              key={b.booking_id}
              data-testid={`driver-job-${b.code}`}
              className="rounded-xl border border-slate-200 bg-white p-6 ambient"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="label-eyebrow">{b.code}</p>
                  <p className="mt-2 text-base font-medium">
                    {b.origin?.name} <ArrowRight size={13} className="inline text-slate-400" />{" "}
                    {b.destination?.name}
                  </p>
                  <p className="mt-1.5 text-sm text-slate-500">
                    {b.pickup_at?.replace("T", " · ").slice(0, 18)} · {b.vehicle_class_name}
                  </p>
                  <div className="mt-3 flex gap-5 text-xs text-slate-600">
                    <span className="inline-flex items-center gap-1.5">
                      <Users size={13} /> {b.pax}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Briefcase size={13} /> {b.luggage}
                    </span>
                    {b.flight_number && <span>{b.flight_number}</span>}
                  </div>
                </div>
                <div className="text-right">
                  <StatusBadge status={b.status} testId={`driver-job-status-${b.code}`} />
                  <p className="mt-3 font-display text-2xl text-sea">{eur(b.price_eur)}</p>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
                {tab === "available" ? (
                  <Button
                    data-testid={`accept-job-${b.code}-btn`}
                    onClick={() => accept(b)}
                    className="bg-sea hover:bg-sea-dark"
                  >
                    {t("driver.accept")}
                  </Button>
                ) : (
                  <>
                    {NEXT_STATUS.map((s) => (
                      <Button
                        key={s}
                        size="sm"
                        variant={b.status === s ? "default" : "outline"}
                        data-testid={`status-${s}-${b.code}-btn`}
                        onClick={() => setStatus(b, s)}
                      >
                        {t(`status.${s}`)}
                      </Button>
                    ))}
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`share-location-${b.code}-btn`}
                      onClick={() => shareLocation(b)}
                    >
                      GPS
                    </Button>
                    {b.phone && (
                      <a href={`tel:${b.phone}`} className="self-center text-sm font-semibold text-sea underline">
                        {b.customer_name} · {b.phone}
                      </a>
                    )}
                  </>
                )}
              </div>
            </article>
          ))}
        </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
